import React, { useEffect, useState } from "react";
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import { get } from '../../utils/Api';
import StaticBox from '../../utils/StaticBox';

import Toolbar from '@mui/material/Toolbar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReplayIcon from '@mui/icons-material/Replay';

import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdbIcon from '@mui/icons-material/Adb';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1565c0',
    color: theme.palette.common.white,
  },
}));

const cssIconButton = {
  minWidth: '45px',
  width: '45px',
  height: '45px',
  borderRadius: '4px',
  padding: 0,
};

const defaultEmptyMessage = 'Não há logs registrados!';

const COLORS = {
  debug: {
    icon: () => AdbIcon,
    iconStyle: { height: '22px', color: '#00ffff', },
    tableRow: { cursor: 'pointer', },
    typography: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 'bold', color: '#00ffff', display: 'inline-block', fontFamily: '"Courier New", Courier, monospace', },
  },
  info: {
    icon: () => CheckCircleIcon,
    iconStyle: { height: '22px', color: '#ffffff', },
    tableRow: { cursor: 'pointer', },
    typography: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 'bold', color: '#ffffff', display: 'inline-block', fontFamily: '"Courier New", Courier, monospace', },
  },
  warning: {
    icon: () => WarningIcon,
    iconStyle: { height: '22px', color: '#ffff00', },
    tableRow: { cursor: 'pointer', },
    typography: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 'bold', color: '#ffff00', display: 'inline-block', fontFamily: '"Courier New", Courier, monospace', },
  },
  error: {
    icon: () => ErrorIcon,
    iconStyle: { height: '22px', color: '#ff0000', },
    tableRow: { cursor: 'pointer', },
    typography: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 'bold', color: '#ff0000', display: 'inline-block', fontFamily: '"Courier New", Courier, monospace', },
  },
};

function EnhancedTableHead() {
  return (
    <TableHead>
      <TableRow>
        <StyledTableCell
          key={'linha_log'}
          sx={{ borderBottomColor: '#0c0c0c', }}>
          <Typography>
            {'Linha do log'}
          </Typography>
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

export default function Logging({
  version,
  setLoading,
  handleMessage,
  setError,
  error,
}) {
  const [refresh,setRefresh] = useState(crypto.randomUUID());

  const [logging, setLogging] = useState([]);
  const [pagina,setPagina] = useState(1);
  const [ultimaPagina,setUltimaPagina] = useState(1);

  const recarregar = () => {
    setPagina(1);
    setRefresh(crypto.randomUUID());
  };

  const proximaPagina = () => {
    const proxima = (pagina < ultimaPagina) ? (pagina + 1) : ultimaPagina;
    setPagina(proxima);
    setRefresh(crypto.randomUUID());
  };

  const paginaAnterior = () => {
    const anterior = (pagina > 1) ? (pagina - 1) : 1;
    setPagina(anterior);
    setRefresh(crypto.randomUUID());
  };

  const temPaginaAnterior = () => {
    return (pagina > 1);
  };

  const temProximaPagina = () => {
    return (pagina < ultimaPagina);
  };

  const toolbar = () => {
    return (
      <Toolbar
        position="static"
      >
        <Box
          sx={{
            flex: 2,
            display: 'flex',
            justifycontent: 'flex-start',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            id="tableTitle"
            component="div"
            sx={{
              marginRight: '10px',
            }}
          >
            {`Página ${pagina}/${ultimaPagina}`}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'right',
            gap: 2,
            ml: 'auto',
          }}
        >
          <Stack direction="row" spacing={1} justifycontent="flex-end">

            {
              temPaginaAnterior() ? (
                <Button
                  sx={cssIconButton}
                  variant="contained"
                  size="large"
                  onClick={paginaAnterior}>
                  <ArrowBackIcon />
                </Button>
              ) : (<></>)
            }
            {
              temProximaPagina() ? (
                <Button
                  sx={cssIconButton}
                  variant="contained"
                  size="large"
                  onClick={proximaPagina}>
                  <ArrowForwardIcon />
                </Button>
              ) : (<></>)
            }

            <Button
              sx={cssIconButton}
              variant="contained"
              size="large"
              color="success"
              onClick={recarregar}>
              <ReplayIcon />
            </Button>

          </Stack>
        </Box>

      </Toolbar>
    );
  };

  useEffect(()=>{
    const getLogging = (parametro_pagina) => {
      if (error) { return; }
      setLoading(true);

      get(`api/logging/${parametro_pagina}`,(json,message,severity)=>{

        if (json.success) {
          setLogging(json.logging);
          setUltimaPagina(json.last_page);
          setLoading(false);
        } else {
          handleMessage(message,severity,'',true);
          setLoading(false);
        }

      },(error,message,severity)=>{
        handleMessage(message,severity,error,true);
        setLoading(false);
      });
    };

    getLogging(pagina);
  },[refresh]);

  return (
    <StaticBox
      version={version}
      setLoading={setLoading}
      handleMessage={handleMessage}
      error={error}
      setError={setError}>
      <React.Fragment>
        {toolbar()}
        <TableContainer component={Paper} sx={{ height: '96%' }}>
          <Table
            sx={{ minWidth: { xs: 0, sm: 750 } }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead />
            <TableBody sx={{ backgroundColor: '#0c0c0c', }}>
              {
                (logging.length === 0) ? (
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        borderBottomColor: '#0c0c0c',
                      }}>
                      {defaultEmptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  logging.map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const IconComponent = COLORS[row.level].icon();

                    return (
                      <TableRow
                        role="checkbox"
                        tabIndex={-1}
                        key={`${row.message}-${index}`}
                        sx={COLORS[row.level].tableRow}>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          sx={{
                            padding: { xs: '16px', sm: '6px 6px 6px 16px' },
                            borderBottomColor: '#0c0c0c',
                          }}>
                          <Stack direction="row" spacing={1}>
                            <IconComponent sx={COLORS[row.level].iconStyle} />
                            <Typography component="span" sx={COLORS[row.level].typography}>
                              {row.message}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )
              }

            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    </StaticBox >
  );
}
